import type { ElementDescriptor, Rect, ScreenshotSelection } from '@dreamteam/chat-protocol';

/** Атрибут, которым помечен корень виджета — его элементы никогда не попадают в контекст. */
export const WIDGET_ATTR = 'data-dreamteam-chat';

const cssEscape = (s: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/[^\w-]/g, (c) => `\\${c}`));

export function isWidgetElement(el: Element | null): boolean {
  return !!el?.closest?.(`[${WIDGET_ATTR}]`);
}

const STABLE_ATTRS = ['data-testid', 'data-test', 'data-qa', 'data-cy', 'name', 'aria-label'];

/** Уникальный и по возможности устойчивый CSS-селектор элемента. */
export function selectorFor(el: Element): string {
  if (el.id && !/^\d|[:\s]/.test(el.id)) {
    const sel = `#${cssEscape(el.id)}`;
    if (safeCount(el.ownerDocument, sel) === 1) return sel;
  }
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur.nodeType === 1 && cur !== cur.ownerDocument.documentElement) {
    let part = cur.tagName.toLowerCase();
    const attr = STABLE_ATTRS.find((a) => cur!.getAttribute(a));
    if (cur.id && !/^\d|[:\s]/.test(cur.id)) {
      part = `#${cssEscape(cur.id)}`;
      parts.unshift(part);
      if (safeCount(el.ownerDocument, parts.join(' > ')) === 1) break;
      cur = cur.parentElement;
      continue;
    }
    if (attr) {
      part += `[${attr}="${cur.getAttribute(attr)!.replace(/"/g, '\\"')}"]`;
    } else {
      const cls = [...cur.classList].filter((c) => /^[a-zA-Z][\w-]{1,40}$/.test(c) && !/\d{3,}/.test(c)).slice(0, 2);
      if (cls.length) part += cls.map((c) => `.${cssEscape(c)}`).join('');
    }
    const parent: Element | null = cur.parentElement;
    if (parent) {
      const same = [...parent.children].filter((c) => c.tagName === cur!.tagName);
      if (same.length > 1 && !attr) part += `:nth-of-type(${same.indexOf(cur) + 1})`;
    }
    parts.unshift(part);
    const sel = parts.join(' > ');
    if (safeCount(el.ownerDocument, sel) === 1) return sel;
    cur = parent;
  }
  return parts.join(' > ');
}

function safeCount(doc: Document, sel: string): number {
  try {
    return doc.querySelectorAll(sel).length;
  } catch {
    return 0;
  }
}

function rectOf(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { x: Math.round(r.x), y: Math.round(r.y), width: Math.round(r.width), height: Math.round(r.height) };
}

function visibleText(el: Element, max = 160): string | undefined {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return undefined;
  const t = ((el as HTMLElement).innerText ?? el.textContent ?? '').replace(/\s+/g, ' ').trim();
  return t ? (t.length > max ? t.slice(0, max - 1) + '…' : t) : undefined;
}

function openTag(el: Element, max = 300): string {
  const attrs = [...el.attributes]
    .filter((a) => a.name !== 'value' && a.name !== 'style' && !a.name.startsWith('on'))
    .map((a) => `${a.name}="${a.value.length > 80 ? a.value.slice(0, 79) + '…' : a.value}"`)
    .join(' ');
  const s = `<${el.tagName.toLowerCase()}${attrs ? ' ' + attrs : ''}>`;
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

/** Описание элемента без значений полей ввода (там могут быть персональные данные). */
export function describeElement(el: Element, relation?: ElementDescriptor['relation']): ElementDescriptor {
  const h = el as HTMLElement & { name?: string; type?: string; placeholder?: string; href?: string };
  const d: ElementDescriptor = { selector: selectorFor(el), tag: el.tagName.toLowerCase(), rect: rectOf(el) };
  if (el.id) d.id = el.id;
  if (el.classList.length) d.classes = [...el.classList].slice(0, 8);
  const role = el.getAttribute('role');
  if (role) d.role = role;
  const aria = el.getAttribute('aria-label');
  if (aria) d.ariaLabel = aria;
  const testId = el.getAttribute('data-testid') ?? el.getAttribute('data-test') ?? el.getAttribute('data-qa');
  if (testId) d.testId = testId;
  if (typeof h.name === 'string' && h.name) d.name = h.name;
  if (typeof h.type === 'string' && h.type && el.tagName !== 'BUTTON') d.type = h.type;
  if (typeof h.placeholder === 'string' && h.placeholder) d.placeholder = h.placeholder;
  const href = el.getAttribute('href');
  if (href) d.href = href.length > 200 ? href.slice(0, 199) + '…' : href;
  const text = visibleText(el);
  if (text) d.text = text;
  d.html = openTag(el);
  if (relation) d.relation = relation;
  return d;
}

function isVisible(el: Element, r: DOMRect): boolean {
  if (r.width < 1 || r.height < 1) return false;
  const cs = getComputedStyle(el);
  const opacity = cs.opacity === '' ? 1 : Number(cs.opacity);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && opacity > 0.01;
}

const contains = (outer: Rect, r: DOMRect) =>
  r.left >= outer.x - 1 && r.top >= outer.y - 1 && r.right <= outer.x + outer.width + 1 && r.bottom <= outer.y + outer.height + 1;

const intersects = (a: Rect, r: DOMRect) => r.left < a.x + a.width && r.right > a.x && r.top < a.y + a.height && r.bottom > a.y;

/** Самый глубокий элемент приложения под точкой (виджет и оверлей пропускаются). */
export function elementAtPoint(doc: Document, x: number, y: number): Element | null {
  const list = doc.elementsFromPoint ? doc.elementsFromPoint(x, y) : [];
  return list.find((e) => !isWidgetElement(e) && e !== doc.documentElement && e !== doc.body) ?? null;
}

/**
 * Собирает DOM-элементы, попавшие в выделенную область:
 *  - target: элемент под точкой клика (начало выделения);
 *  - inside: элементы, целиком лежащие внутри прямоугольника (самые «содержательные» — листья и элементы с текстом/ролью);
 *  - container: ближайший общий предок, полностью покрывающий область.
 */
export function collectSelection(
  doc: Document,
  rect: Rect,
  point: { x: number; y: number },
  opts: { maxElements?: number } = {},
): Omit<ScreenshotSelection, 'scroll' | 'viewport' | 'url'> {
  const max = opts.maxElements ?? 150;
  const targetEl = elementAtPoint(doc, point.x, point.y);
  const inside: Element[] = [];
  let truncated = false;
  const all = doc.body ? doc.body.getElementsByTagName('*') : ([] as unknown as HTMLCollectionOf<Element>);
  const limit = Math.min(all.length, 20000);
  for (let i = 0; i < limit; i++) {
    const el = all[i];
    if (el.hasAttribute(WIDGET_ATTR) || isWidgetElement(el)) continue;
    const tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
    // внутренности SVG (path, g…) — шум; сам <svg> оставляем
    if (el.namespaceURI === 'http://www.w3.org/2000/svg' && tag.toLowerCase() !== 'svg') continue;
    const r = el.getBoundingClientRect();
    if (!intersects(rect, r) || !contains(rect, r) || !isVisible(el, r)) continue;
    inside.push(el);
  }
  // Оставляем «значимые» элементы: интерактивные, с ролью/testid, либо листья с текстом.
  const insideSet = new Set(inside);
  const meaningful = inside.filter((el) => {
    if (/^(A|BUTTON|INPUT|SELECT|TEXTAREA|LABEL|IMG|SVG|H[1-6]|TD|TH|LI|OPTION)$/.test(el.tagName.toUpperCase())) return true;
    if (el.getAttribute('role') || el.getAttribute('data-testid') || el.getAttribute('aria-label')) return true;
    const hasElementChildren = [...el.children].some((c) => insideSet.has(c));
    return !hasElementChildren && !!visibleText(el, 20);
  });
  const picked = meaningful.length ? meaningful : inside;
  if (picked.length > max) truncated = true;

  // Контейнер: поднимаемся от target (или первого элемента), пока не покроем всю область.
  let container: Element | null = targetEl ?? picked[0] ?? null;
  while (container && container !== doc.body) {
    const r = container.getBoundingClientRect();
    if (r.left <= rect.x + 1 && r.top <= rect.y + 1 && r.right >= rect.x + rect.width - 1 && r.bottom >= rect.y + rect.height - 1) break;
    container = container.parentElement;
  }

  const elements: ElementDescriptor[] = [];
  if (container && container !== doc.body && container !== doc.documentElement) elements.push({ ...describeElement(container, 'container'), text: undefined });
  for (const el of picked.slice(0, max)) elements.push(describeElement(el, 'inside'));

  return {
    rect,
    point,
    target: targetEl ? describeElement(targetEl, 'target') : null,
    elements,
    truncated,
  };
}
